import Hammer from 'hammerjs'

export default class PinchZoomImage {
	constructor(element) {
		this.element = element
		this.index = Number(element.dataset.index)
		this.hammer = new Hammer(this.element)
		this.originalSize = {
			width: document.documentElement.clientWidth,
			height: document.documentElement.clientHeight,
		}
		this.current = {
			x: -document.documentElement.clientWidth / 2,
			y: -document.documentElement.clientHeight / 2,
			z: 1,
			zooming: false,
			width: this.originalSize.width * 1,
			height: this.originalSize.height * 1,
		}
		this.last = {
			x: this.current.x,
			y: this.current.y,
			z: this.current.z,
		}
		this.scaleLimit = {
			max: 2,
			min: 1,
		}
		this.isImageLandscape =
			this.element.naturalWidth >= this.element.naturalHeight
		this.translateLimit = {
			x: {
				max: this.isImageLandscape ? 0.25 : 0,
				min: this.isImageLandscape ? -0.75 : -0.5,
			},
			y: {
				max: 0,
				min: -0.5,
			},
		}
		this.isOriginalSize = true
		this.pinchStart = { x: 0, y: 0 }
		this.fixHammerjsDeltaIssue = { x: 0, y: 0 }
		this.pinchZoomOrigin = { x: 0, y: 0 }
		this.lastEvent = null

		this.listenTouchEvents()
	}

	getRelativePosition(point, scale) {
		const domCoords = this.getCoords(this.element)
		const elementX = point.x - domCoords.x
		const elementY = point.y - domCoords.y
		const relativeX = elementX / ((this.originalSize.width * scale) / 2) - 1
		const relativeY = elementY / ((this.originalSize.height * scale) / 2) - 1
		return { x: relativeX, y: relativeY }
	}

	// eslint-disable-next-line
	getCoords(elem) {
		// crossbrowser version
		const box = elem.getBoundingClientRect()
		const { body } = document
		const docEl = document.documentElement
		const scrollTop = window.pageYOffset || docEl.scrollTop || body.scrollTop
		const scrollLeft = window.pageXOffset || docEl.scrollLeft || body.scrollLeft
		const clientTop = docEl.clientTop || body.clientTop || 0
		const clientLeft = docEl.clientLeft || body.clientLeft || 0
		const top = box.top + scrollTop - clientTop
		const left = box.left + scrollLeft - clientLeft
		return { x: Math.round(left), y: Math.round(top) }
	}

	scaleFrom(zoomOrigin, currentScale, newScale) {
		const currentShift = this.getCoordinateShiftDueToScale(
			this.originalSize,
			currentScale,
		)
		const newShift = this.getCoordinateShiftDueToScale(
			this.originalSize,
			newScale,
		)
		const zoomDistance = newScale - currentScale

		const shift = {
			x: currentShift.x - newShift.x,
			y: currentShift.y - newShift.y,
		}
		const output = {
			x: zoomOrigin.x * shift.x,
			y: zoomOrigin.y * shift.y,
			z: zoomDistance,
		}
		return output
	}

	// eslint-disable-next-line
	getCoordinateShiftDueToScale(size, scale) {
		const newWidth = scale * size.width
		const newHeight = scale * size.height
		const dx = (newWidth - size.width) / 2
		const dy = (newHeight - size.height) / 2
		return {
			x: dx,
			y: dy,
		}
	}

	getTranslatePercent() {
		return {
			x: this.current.x / (this.originalSize.width * this.current.z),
			y: this.current.y / (this.originalSize.height * this.current.z),
		}
	}

	limitBounary() {
		const { x: translatePercentX, y: translatePercentY } =
			this.getTranslatePercent()
		const finalRatio = { x: translatePercentX, y: translatePercentY }
		if (translatePercentX > this.translateLimit.x.max) {
			finalRatio.x = this.translateLimit.x.max
		}
		if (translatePercentX < this.translateLimit.x.min) {
			finalRatio.x = this.translateLimit.x.min
		}
		if (translatePercentY > this.translateLimit.y.max) {
			finalRatio.y = this.translateLimit.y.max
		}
		if (translatePercentY < this.translateLimit.y.min) {
			finalRatio.y = this.translateLimit.y.min
		}

		if (
			finalRatio.x === translatePercentX &&
			finalRatio.y === translatePercentY
		)
			return

		const params = {
			x:
				finalRatio.x === translatePercentX
					? this.current.x
					: finalRatio.x * this.originalSize.width * this.current.z,
			y:
				finalRatio.y === translatePercentY
					? this.current.y
					: finalRatio.y * this.originalSize.height * this.current.z,
			z: this.current.z,
		}

		this.current.x = params.x
		this.current.y = params.y
		this.current.z = params.z

		this.last = {
			...params,
		}
	}

	update() {
		this.current.height = this.originalSize.height * this.current.z
		this.current.width = this.originalSize.width * this.current.z
		if (this.current.z < this.scaleLimit.min) {
			this.current.z = this.scaleLimit.min
			this.reset()
			return
		}

		if (this.current.z > this.scaleLimit.max) {
			this.current.z = this.scaleLimit.max
			return
		}

		this.limitBounary()
		this.element.style.transform = `translate3d(${this.current.x}px, ${this.current.y}px, 0) scale(${this.current.z})`
	}

	reset() {
		const params = {
			x: -document.documentElement.clientWidth / 2,
			y: -document.documentElement.clientHeight / 2,
			z: 1,
		}
		this.last = {
			...params,
		}
		this.isOriginalSize = true
		this.element.style.left = '50%'
		this.element.style.top = '50%'
		this.element.style.transform = `translate3d(-50%, -50%, 0) scale(${this.current.z})`
	}

	listenTouchEvents() {
		this.hammer.get('pinch').set({ enable: true })
		this.hammer.get('pan').set({ threshold: 0 })
		this.hammer.on('doubletap', e => {
			let scaleFactor = 1
			if (this.current.zooming === false) {
				this.current.zooming = true
			} else {
				this.current.zooming = false
				scaleFactor = -scaleFactor
			}
			this.element.style.transition = '0.3s'
			setTimeout(() => {
				this.element.style.transition = 'none'
			}, 300)
			const zoomOrigin = this.getRelativePosition(
				{ x: e.center.x, y: e.center.y },
				this.current.z,
			)
			const d = this.scaleFrom(
				zoomOrigin,
				this.current.z,
				this.current.z + scaleFactor,
			)
			this.current.x += d.x
			this.current.y += d.y
			this.current.z += d.z
			this.last.x = this.current.x
			this.last.y = this.current.y
			this.last.z = this.current.z
			this.update()
		})
		this.hammer.on('pan', e => {
			if (this.isOriginalSize) return
			if (this.lastEvent !== 'pan') {
				this.fixHammerjsDeltaIssue = {
					x: e.deltaX,
					y: e.deltaY,
				}
			}
			this.current.x = this.last.x + e.deltaX - this.fixHammerjsDeltaIssue.x
			this.current.y = this.last.y + e.deltaY - this.fixHammerjsDeltaIssue.y
			this.lastEvent = 'pan'
			this.update()
		})
		this.hammer.on('pinch', e => {
			this.isOriginalSize = false
			const d = this.scaleFrom(
				this.pinchZoomOrigin,
				this.last.z,
				this.last.z * e.scale,
			)
			this.current.x = d.x + this.last.x + e.deltaX
			this.current.y = d.y + this.last.y + e.deltaY
			this.current.z = d.z + this.last.z
			this.lastEvent = 'pinch'
			this.update()
		})
		this.hammer.on('pinchstart', e => {
			this.pinchStart.x = e.center.x
			this.pinchStart.y = e.center.y
			this.pinchZoomOrigin = this.getRelativePosition(
				{ x: this.pinchStart.x, y: this.pinchStart.y },
				this.current.z,
			)
			this.lastEvent = 'pinchstart'
		})
		this.hammer.on('panend', () => {
			this.last.x = this.current.x
			this.last.y = this.current.y
			this.lastEvent = 'panend'
		})
		this.hammer.on('pinchend', () => {
			this.last.x = this.current.x
			this.last.y = this.current.y
			this.last.z = this.current.z
			this.lastEvent = 'pinchend'
		})
	}
}
